import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { GET_PARENT } from '@/modules/API/parent/parentHandler'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useTranslation } from 'react-i18next'
import styles from './parentProfile.module.scss'
import Title from '@/components/_custom/Title/title'
import UserInput from '@/components/_custom/UserInput/userInput'
import { UPDATE_ACCOUNT, SET_DATA_SHARING_CONSENT } from '@/modules/API/account/accountHandler'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { ArrowForward, KeyboardArrowUp } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import ProfileAddress from '../profileAddress/profileAddress'
import DeactiveAccountDialog from '../deactiveAccount/deactiveAccountDialog.tsx'
import ChangeLanguageDialog from '../../../home/Dialog/changeLanguage.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface parentProfileProps {
    signUp?: boolean
    signUpAutolink?: boolean
}

const ParentProfile: FunctionComponent<parentProfileProps> = ({ signUp, signUpAutolink }: parentProfileProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [userId, setUserId] = useState<number>(0)

    const [email, setEmail] = useState<string>('')
    const [firstname, setFirstname] = useState<string>('')
    const [lastname, setLastname] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [phoneSecond, setPhoneSecond] = useState<string>('')
    const [address, setAddress] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [zip, setZip] = useState<string>('')
    const [country, setCountry] = useState<string>('')
    const [addressSecond, setAddressSecond] = useState<string>('')
    const [citySecond, setCitySecond] = useState<string>('')
    const [zipSecond, setZipSecond] = useState<string>('')
    const [countrySecond, setCountrySecond] = useState<string>('')

    const [addPhoneSecond, setAddPhoneSecond] = useState<boolean>(false)
    const [addAddressSecond, setAddAddressSecond] = useState<boolean>(false)
    const [dataSharingConsent, setDataSharingConsent] = useState<boolean>(false)

    const [isDeactiveAccountOpen, setDeactiveAccountOpen] = useState<boolean>(false)

    const {
        loading: loadingParent,
        error: errorParent,
        data: dataParent,
        refetch: refetchParent
    } = useQuery(GET_PARENT)

    useEffect(() => {
        if (dataParent) {
            setUserId(dataParent.getParent.userId)
            setEmail(dataParent.getParent.email)
            setFirstname(dataParent.getParent.firstname)
            setLastname(dataParent.getParent.lastname)
            setPhone(dataParent.getParent.phone)
            setPhoneSecond(dataParent.getParent.phoneSecond)
            setAddress(dataParent.getParent.address)
            setCity(dataParent.getParent.city)
            setZip(dataParent.getParent.zip)
            setCountry(dataParent.getParent.country)
            setAddressSecond(dataParent.getParent.addressSecond)
            setCitySecond(dataParent.getParent.citySecond)
            setZipSecond(dataParent.getParent.zipSecond)
            setCountrySecond(dataParent.getParent.countrySecond)
            if (
                dataParent.getParent.addressSecond ||
                dataParent.getParent.citySecond ||
                dataParent.getParent.zipSecond ||
                dataParent.getParent.countrySecond
            ) {
                setAddAddressSecond(true)
            }
            if (dataParent.getParent.phoneSecond) {
                setAddPhoneSecond(true)
            }
            setDataSharingConsent(dataParent.getParent.dataSharingConsent || false)
        }
    }, [dataParent])

    const [setDataSharingConsentMutation] = useMutation(SET_DATA_SHARING_CONSENT, {
        onCompleted: () => showNotificationSuccess(t('profileUpdated')),
        onError: () => showNotificationError(t('profileUpdatedError'))
    })

    const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
        variables: {
            firstname,
            lastname,
            phone,
            phoneSecond: phoneSecond || undefined,
            address,
            city,
            zip,
            country,
            addressSecond,
            citySecond,
            zipSecond,
            countrySecond
        },
        onCompleted: async (): Promise<void> => {
            if (signUp) {
                navigate('/account/childs?account=signup')
            }
            if (signUpAutolink) {
                navigate('/account/childs?account=signupAutolink')
            } else {
                showNotificationSuccess(t('profileUpdated'))
                await refetchParent()
            }
        },
        onError: (): void => {
            showNotificationError(t('profileUpdatedError'))
        }
    })

    function handleDataSharingConsent(value: boolean): void {
        setDataSharingConsent(value)
        setDataSharingConsentMutation({ variables: { consent: value } })
    }

    function backToHome(): void {
        navigate('/home')
    }

    const [isLanguageDialog, setLanguageDialog] = useState<boolean>(true)

    const fieldsEmpty: boolean = !firstname || !lastname || !phone || !address || !city || !zip || !country

    const fieldsUnchanged: boolean =
        dataParent && dataParent.getParent
            ? dataParent.getParent.email === email &&
              dataParent.getParent.firstname === firstname &&
              dataParent.getParent.lastname === lastname &&
              dataParent.getParent.phone === phone &&
              dataParent.getParent.phoneSecond === phoneSecond &&
              dataParent.getParent.address === address &&
              dataParent.getParent.city === city &&
              dataParent.getParent.zip === zip &&
              dataParent.getParent.country === country &&
              dataParent.getParent.addressSecond === addressSecond &&
              dataParent.getParent.citySecond === citySecond &&
              dataParent.getParent.zipSecond === zipSecond &&
              dataParent.getParent.countrySecond === countrySecond
            : true

    const checkDisabled: boolean = fieldsEmpty || fieldsUnchanged

    return (
        <>
            {loadingParent && <Loading overall />}
            <div className={styles.PagePosition}>
                <div className={styles.ProfilPlacement}>
                    <Checkbox
                        text={t('dataSharingConsent')}
                        checked={dataSharingConsent}
                        onChange={handleDataSharingConsent}
                        parent
                    />
                </div>
                <div className={styles.ProfilPlacement}>
                    {dataParent && (
                        <div className={styles.Form}>
                            <div className={styles.Title}>
                                <Title text={t('personalInformation')} />
                            </div>
                            <div className={styles.InputContainer}>
                                <div className={styles.Input}>
                                    <UserInput
                                        label={t('lastname')}
                                        value={lastname}
                                        setValue={setLastname}
                                        type='text'
                                        required
                                        parent
                                        fullWidth
                                        format={'upper'}
                                        className={styles.UserInput}
                                        unallowedCharacters={'num'}
                                    />
                                    <UserInput
                                        label={t('firstname')}
                                        value={firstname}
                                        setValue={setFirstname}
                                        type='text'
                                        required
                                        parent
                                        fullWidth
                                        format={'pascal'}
                                        className={styles.UserInput}
                                        unallowedCharacters={'num'}
                                    />
                                </div>
                                <div className={styles.Input}>
                                    <UserInput
                                        label={t('email')}
                                        value={email}
                                        setValue={setEmail}
                                        type='text'
                                        disabled
                                        required
                                        parent
                                        fullWidth
                                        className={styles.UserInput}
                                    />
                                    <UserInput
                                        label={t('phone')}
                                        value={phone}
                                        setValue={setPhone}
                                        type='text'
                                        verify={'phone'}
                                        required
                                        parent
                                        fullWidth
                                        format={'phone'}
                                        className={styles.UserInput}
                                    />
                                </div>
                                {addPhoneSecond && (
                                    <div className={styles.Input}>
                                        <UserInput
                                            label={t('phoneSecond')}
                                            value={phoneSecond}
                                            setValue={setPhoneSecond}
                                            type='text'
                                            verify={'phone'}
                                            parent
                                            fullWidth
                                            format={'phone'}
                                            className={styles.UserInputAlone}
                                        />
                                    </div>
                                )}
                                {!addPhoneSecond && (
                                    <div className={styles.secondAddressContainer}>
                                        <a className={styles.secondAddress} onClick={() => setAddPhoneSecond(true)}>
                                            {t('addSecondPhone')}
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className={styles.Title}>
                                <Title text={t('address')} />
                            </div>
                            <div className={styles.InputContainer}>
                                <ProfileAddress
                                    signUp={signUp || signUpAutolink}
                                    parent={true}
                                    address={address}
                                    setAddress={setAddress}
                                    city={city}
                                    setCity={setCity}
                                    zip={zip}
                                    setZip={setZip}
                                    country={country}
                                    setCountry={setCountry}
                                />
                                {city !== '' && zip !== '' && country !== '' && (
                                    <>
                                        {!addAddressSecond ? (
                                            <div className={styles.secondAddressContainer}>
                                                <a
                                                    className={styles.secondAddress}
                                                    onClick={() => setAddAddressSecond(true)}
                                                >
                                                    {t('addSecondAddress')}
                                                </a>
                                            </div>
                                        ) : (
                                            <>
                                                <div className={styles.TitleAndIcon}>
                                                    <Title text={t('addressSecond')} />
                                                    <KeyboardArrowUp
                                                        className={styles.Icon}
                                                        onClick={() => setAddAddressSecond(false)}
                                                    />
                                                </div>
                                                <div className={styles.Input}>
                                                    <UserInput
                                                        label={t('addressSecond')}
                                                        value={addressSecond}
                                                        setValue={setAddressSecond}
                                                        type='text'
                                                        parent
                                                        fullWidth
                                                        className={styles.UserInput}
                                                        unallowedCharacters={'special'}
                                                    />
                                                    <UserInput
                                                        label={t('citySecond')}
                                                        value={citySecond}
                                                        setValue={setCitySecond}
                                                        type='text'
                                                        parent
                                                        fullWidth
                                                        className={styles.UserInput}
                                                        format={'pascal'}
                                                        unallowedCharacters={'num'}
                                                    />
                                                </div>
                                                <div className={styles.Input}>
                                                    <UserInput
                                                        label={t('zipCodeSecond')}
                                                        value={zipSecond}
                                                        setValue={setZipSecond}
                                                        type='text'
                                                        parent
                                                        fullWidth
                                                        className={styles.UserInput}
                                                        format={'zipCode'}
                                                        unallowedCharacters={'alpha'}
                                                    />
                                                    <UserInput
                                                        label={t('countrySecond')}
                                                        value={countrySecond}
                                                        setValue={setCountrySecond}
                                                        type='text'
                                                        parent
                                                        fullWidth
                                                        className={styles.UserInput}
                                                        format={'upper'}
                                                        unallowedCharacters={'num'}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    {errorParent && <ErrorMessage error={t('generalError')} />}
                </div>
                <div className={styles.ProfilPlacement}>
                    {!signUp && !signUpAutolink ? (
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon type={'return'} onClick={backToHome} parent className={styles.Button} />
                            <ButtonTextIcon
                                type={'update'}
                                onClick={updateAccount}
                                parent
                                className={styles.Button}
                                disabled={checkDisabled}
                            />
                        </div>
                    ) : (
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('continue')}
                            customIcon={<ArrowForward />}
                            onClick={updateAccount}
                            parent
                            className={styles.Button}
                            disabled={checkDisabled}
                        />
                    )}
                </div>
                {!signUp && !signUpAutolink && (
                    <div className={styles.ProfilPlacement}>
                        <ButtonTextIcon
                            type={'custom'}
                            parent
                            customText={t('deleteMyAccount')}
                            className={styles.ButtonDeleteAccount}
                            customIcon={<DeleteIcon />}
                            onClick={() => setDeactiveAccountOpen(true)}
                        />
                    </div>
                )}
            </div>
            {isDeactiveAccountOpen && (
                <DeactiveAccountDialog
                    open={isDeactiveAccountOpen}
                    handleClose={() => setDeactiveAccountOpen(false)}
                    userId={userId}
                    isParent={true}
                />
            )}
            {(signUp || signUpAutolink) && isLanguageDialog && (
                <ChangeLanguageDialog
                    open={isLanguageDialog}
                    handleClose={() => setLanguageDialog(false)}
                    parent={true}
                    signUp={signUp || signUpAutolink}
                />
            )}
        </>
    )
}

export default ParentProfile
